/**
 * Environment Controller — manages THREE.js environment objects in the Giro3D scene.
 *
 * Architecture: environment-taxonomy.md §3, feature spec §4.3, §4.5, §6.3
 * Pattern: pure exported factory function (follows lorScopeManager.ts pattern).
 *
 * Integration points (for Giro3DAdapter wiring — do NOT modify the adapter directly):
 *   - mount(): call createEnvironmentController(scene, instance) after lighting setup
 *   - addLayer(): if layer.kind === 'environment', call controller.applyEnvironmentLayer(...)
 *   - setLayerOpacity(): if env layer, call controller.setEnvironmentLayerOpacity(...)
 *   - setLayerVisible(): if env layer, call controller.setEnvironmentLayerVisible(...)
 *   - unmount(): call controller.dispose()
 *
 * Coordinate system: Z is UP (EPSG:3857 scene). X/Y are horizontal.
 * All positioned effects are placed relative to the camera so they remain
 * visible at large EPSG:3857 coordinate values (e.g. 15 550 000, 4 260 000).
 */

import * as THREE from 'three';
import {
  computeSolarPosition,
  computeLunarPosition,
  solarPositionToDirectionalLightPosition,
} from './solarPosition';
import { tokens } from '@/design/tokens';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EnvironmentKind =
  | 'sky'
  | 'sun'
  | 'moon'
  | 'stars'
  | 'fog'
  | 'clouds'
  | 'wind'
  | 'rain'
  | 'snow'
  | 'lightning'
  | 'darkmode'
  | 'time-of-day';

export interface EnvironmentController {
  applyEnvironmentLayer(
    layerId: string,
    kind: EnvironmentKind,
    params: Record<string, unknown>,
    options?: { visible?: boolean },
  ): void;
  setEnvironmentParameter(layerId: string, key: string, value: unknown): void;
  setEnvironmentLayerVisible(layerId: string, visible: boolean): void;
  setEnvironmentLayerOpacity(layerId: string, opacity: number): void;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Color constants — derived from tokens.color.environment.*
// ---------------------------------------------------------------------------

function hexTokenToInt(hex: string): number {
  return parseInt(hex.slice(1), 16);
}

const SKY_CLEAR = hexTokenToInt(tokens.color.environment.sky.clear);
const SKY_OVERCAST = hexTokenToInt(tokens.color.environment.sky.overcast);
const SKY_DUSK = hexTokenToInt(tokens.color.environment.sky.dusk);
const SKY_NIGHT = hexTokenToInt(tokens.color.environment.sky.night);

const SUN_WARM = hexTokenToInt(tokens.color.environment.sun.warm);
const SUN_NEUTRAL = hexTokenToInt(tokens.color.environment.sun.neutral);
const SUN_COOL = hexTokenToInt(tokens.color.environment.sun.cool);

const MOON_COOL = hexTokenToInt(tokens.color.environment.moon.cool);
const MOON_NEUTRAL = hexTokenToInt(tokens.color.environment.moon.neutral);

// Engine-specific particle colors (no token mapping — governed waiver for generics)
const RAIN_COLOR = 0xaaaacc;
const SNOW_COLOR = 0xeeeeff;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RAIN_POOL_SIZE = 50_000;
const SNOW_POOL_SIZE = 30_000;
const WIND_POOL_SIZE = 10_000;
// tokens.motion.duration.particleFade = 600ms (applied at integration layer)
const STAR_RADIUS_FACTOR = 0.9;
const CLOUD_SPHERE_RADIUS = 10_000;
const LIGHTNING_RAMP_MS = 50;
const LIGHTNING_DECAY_MS = 200;

// ---------------------------------------------------------------------------
// Internal state per-layer
// ---------------------------------------------------------------------------

interface LayerState {
  kind: EnvironmentKind;
  params: Record<string, unknown>;
  visible: boolean;
  opacity: number;
}

interface SunState {
  light: THREE.DirectionalLight;
  target: THREE.Object3D;
}

interface MoonState {
  light: THREE.DirectionalLight;
}

interface StarsState {
  points: THREE.Points;
  geometry: THREE.BufferGeometry;
  material: THREE.PointsMaterial;
}

interface FogState {
  originalFog: THREE.FogExp2 | THREE.Fog | null;
}

interface CloudsState {
  mesh: THREE.Mesh;
  geometry: THREE.SphereGeometry;
  material: THREE.MeshBasicMaterial;
}

interface ParticleState {
  container: THREE.Group;
  mesh: THREE.InstancedMesh;
  geometry: THREE.BufferGeometry;
  material: THREE.MeshBasicMaterial;
  positions: Float32Array;
  velocities: Float32Array;
  count: number;
}

interface WindState {
  container: THREE.Group;
  mesh: THREE.InstancedMesh;
  geometry: THREE.BufferGeometry;
  material: THREE.MeshBasicMaterial;
  positions: Float32Array;
  count: number;
}

interface LightningState {
  ambient: THREE.AmbientLight;
  schedulerHandle: ReturnType<typeof setTimeout> | null;
  flashHandle: number | null;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createEnvironmentController(
  scene: THREE.Scene,
  instance: unknown,
): EnvironmentController {
  const layers = new Map<string, LayerState>();

  let sunState: SunState | null = null;
  let moonState: MoonState | null = null;
  let starsState: StarsState | null = null;
  let fogState: FogState | null = null;
  let originalSceneBackground: THREE.Color | THREE.Texture | null = null;
  let skyBackgroundSaved = false;
  let cloudsState: CloudsState | null = null;
  let rainState: ParticleState | null = null;
  let snowState: ParticleState | null = null;
  let windState: WindState | null = null;
  let lightningState: LightningState | null = null;

  let todAnimationHandle: number | null = null;
  let rainAnimFrame: number | null = null;
  let snowAnimFrame: number | null = null;
  let windAnimFrame: number | null = null;
  let cloudAnimFrame: number | null = null;
  let starTwinkleFrame: number | null = null;
  let disposed = false;
  const reducedMotion = typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Project location (defaults to Tokyo)
  const projectLat = 35.6762;
  const projectLon = 139.6503;

  // Darkmode saved state
  let darkmodeSnapshot: Map<string, { visible: boolean; params: Record<string, unknown> }> | null = null;

  const inst = instance as { notifyChange?: () => void; scene?: THREE.Scene; view?: { camera?: THREE.Camera; controls?: { target?: THREE.Vector3 } } };
  const _sharedDummy = new THREE.Object3D();
  const _sharedWorldPos = new THREE.Vector3();

  function getCamera(): THREE.Camera | null {
    const cam = inst.view?.camera;
    if (cam) return cam;
    let found: THREE.Camera | null = null;
    scene.traverse((child) => {
      if (child instanceof THREE.Camera) found = child;
    });
    return found;
  }

  /** Orbit distance (m) — matches the "camera height" chip, not raw Web Mercator Z. */
  function getViewDistance(): number {
    const cam = getCamera();
    const target = inst.view?.controls?.target;
    if (cam && target) {
      return Math.max(20, cam.position.distanceTo(target));
    }
    return 400;
  }

  /**
   * Legacy helper for world-anchored effects (clouds). Rain/snow/wind use
   * camera-local coordinates instead — see getViewDistance().
   */
  function getCameraPosition(): THREE.Vector3 {
    const cam = getCamera();
    if (cam) {
      cam.getWorldPosition(_sharedWorldPos);
      return _sharedWorldPos.clone();
    }
    return new THREE.Vector3(0, 0, 0);
  }

  function getLayerParam(kind: EnvironmentKind, key: string): unknown {
    for (const state of layers.values()) {
      if (state.kind === kind) return state.params[key];
    }
    return undefined;
  }

  function getLayerParamNum(kind: EnvironmentKind, key: string, fallback: number): number {
    const v = getLayerParam(kind, key);
    return typeof v === 'number' ? v : fallback;
  }

  function getWindParams(): { speed: number; vx: number; vy: number } {
    const speed = getLayerParamNum('wind', 'speed', 5);
    const dir = getLayerParamNum('wind', 'direction', 270) * Math.PI / 180;
    return { speed: Math.max(speed, 0.1), vx: Math.sin(dir) * speed, vy: Math.cos(dir) * speed };
  }

  function createCloudTexture(): THREE.Texture | null {
    if (typeof document === 'undefined') return null;
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < 120; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = 24 + Math.random() * 96;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, 'rgba(255,255,255,0.85)');
      grad.addColorStop(0.5, 'rgba(255,255,255,0.35)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 2);
    return texture;
  }

  function notifyChange(): void {
    inst.notifyChange?.();
  }

  // ------- SKY -------
  function applySky(params: Record<string, unknown>): void {
    const mode = (params['mode'] as string) ?? 'clear';

    if (!skyBackgroundSaved) {
      originalSceneBackground = scene.background as THREE.Color | THREE.Texture | null;
      skyBackgroundSaved = true;
    }

    const skyColors: Record<string, number> = {
      clear: SKY_CLEAR,
      overcast: SKY_OVERCAST,
      dusk: SKY_DUSK,
      night: SKY_NIGHT,
      custom: SKY_CLEAR,
    };

    scene.background = new THREE.Color(skyColors[mode] ?? SKY_CLEAR);
    notifyChange();
  }

  function disposeSky(): void {
    if (skyBackgroundSaved) {
      scene.background = originalSceneBackground;
      originalSceneBackground = null;
      skyBackgroundSaved = false;
      notifyChange();
    }
  }

  // ------- SUN -------
  function createSun(params: Record<string, unknown>): void {
    if (sunState) { console.debug('[EnvCtrl] createSun: already exists, skip'); return; }
    console.info('[EnvCtrl] createSun: creating DirectionalLight');

    const colorPreset = (params['color'] as string) ?? 'neutral';
    const intensity = (params['intensity'] as number) ?? 2.0;
    const castShadows = (params['castShadows'] as boolean) ?? true;
    const shadowMapSize = (params['shadowMapSize'] as number) ?? 2048;

    const colors: Record<string, number> = { warm: SUN_WARM, neutral: SUN_NEUTRAL, cool: SUN_COOL };
    const light = new THREE.DirectionalLight(colors[colorPreset] ?? SUN_NEUTRAL, intensity);

    if (castShadows) {
      light.castShadow = true;
      light.shadow.mapSize.set(shadowMapSize, shadowMapSize);
      light.shadow.camera.near = 1;
      light.shadow.camera.far = 5000;
      light.shadow.camera.left = -500;
      light.shadow.camera.right = 500;
      light.shadow.camera.top = 500;
      light.shadow.camera.bottom = -500;
      light.shadow.bias = -0.001;
    }

    const target = new THREE.Object3D();
    light.target = target;

    scene.add(light);
    scene.add(target);
    sunState = { light, target };

    updateSunPosition(params);
  }

  function updateSunPosition(params: Record<string, unknown>): void {
    if (!sunState) return;

    const hour = (params['hour'] as number) ?? 13.0;
    const now = new Date();
    now.setHours(Math.floor(hour), (hour % 1) * 60, 0, 0);

    const solar = computeSolarPosition(now, projectLat, projectLon);
    const dir = solarPositionToDirectionalLightPosition(solar, 10000);
    const camPos = getCameraPosition();
    sunState.light.position.set(camPos.x + dir.x, camPos.y + dir.y, camPos.z + dir.z);
    sunState.light.target.position.set(camPos.x, camPos.y, camPos.z);
    sunState.light.target.updateMatrixWorld();

    if (solar.elevation < 0) {
      sunState.light.intensity = 0;
    }
    notifyChange();
  }

  function disposeSun(): void {
    if (!sunState) return;
    scene.remove(sunState.light);
    scene.remove(sunState.target);
    if (sunState.light.shadow.map) {
      sunState.light.shadow.map.dispose();
    }
    sunState = null;
  }

  // ------- MOON -------
  function createMoon(params: Record<string, unknown>): void {
    if (moonState) { console.debug('[EnvCtrl] createMoon: already exists, skip'); return; }
    console.info('[EnvCtrl] createMoon: creating DirectionalLight');

    const colorPreset = (params['color'] as string) ?? 'coldBlue';
    const intensityMul = (params['intensity'] as number) ?? 0.5;
    const phase = (params['phase'] as number) ?? 0.5;

    const colors: Record<string, number> = { coldBlue: MOON_COOL, neutral: MOON_NEUTRAL };
    const baseIntensity = 0.003;
    const light = new THREE.DirectionalLight(
      colors[colorPreset] ?? MOON_COOL,
      baseIntensity * intensityMul * Math.sin(phase * Math.PI),
    );
    light.castShadow = false;

    scene.add(light);
    moonState = { light };
    updateMoonPosition();
  }

  function updateMoonPosition(): void {
    if (!moonState) return;
    const now = new Date();
    const lunar = computeLunarPosition(now, projectLat, projectLon);
    const pos = solarPositionToDirectionalLightPosition(lunar, 10000);
    const camPos = getCameraPosition();
    moonState.light.position.set(camPos.x + pos.x, camPos.y + pos.y, camPos.z + pos.z);
    notifyChange();
  }

  function disposeMoon(): void {
    if (!moonState) return;
    scene.remove(moonState.light);
    moonState = null;
  }

  // ------- STARS -------
  function createStars(params: Record<string, unknown>): void {
    if (starsState) { console.debug('[EnvCtrl] createStars: already exists, skip'); return; }
    console.info('[EnvCtrl] createStars: creating Points');

    const density = (params['density'] as string) ?? 'medium';
    const counts: Record<string, number> = { low: 500, medium: 1600, high: 5000 };
    const count = counts[density] ?? 1600;

    const positions = new Float32Array(count * 3);
    const radius = (scene as any)?.fog?.far ?? 10000;
    const starRadius = radius * STAR_RADIUS_FACTOR;

    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = starRadius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = starRadius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = starRadius * Math.cos(phi);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 5,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.renderOrder = -1;
    const camPos = getCameraPosition();
    points.position.set(camPos.x, camPos.y, camPos.z);
    scene.add(points);
    starsState = { points, geometry, material };
    notifyChange();
    const twinkle = (params['twinkle'] as boolean) ?? true;
    if (twinkle && !reducedMotion) startStarTwinkle();
  }

  function startStarTwinkle(): void {
    const t0 = performance.now();
    function animateStars(): void {
      if (!starsState || disposed) { starTwinkleFrame = null; return; }
      const elapsed = (performance.now() - t0) / 1000;
      starsState.material.opacity = 0.8 + 0.2 * Math.sin(elapsed * 1.5);
      notifyChange();
      starTwinkleFrame = requestAnimationFrame(animateStars);
    }
    starTwinkleFrame = requestAnimationFrame(animateStars);
  }

  function disposeStars(): void {
    if (starTwinkleFrame !== null) { cancelAnimationFrame(starTwinkleFrame); starTwinkleFrame = null; }
    if (!starsState) return;
    scene.remove(starsState.points);
    starsState.geometry.dispose();
    starsState.material.dispose();
    starsState = null;
  }

  // ------- FOG -------
  function applyFog(params: Record<string, unknown>): void {
    const density = (params['density'] as number) ?? 0.0002;
    const colour = (params['colour'] as string) ?? tokens.color.environment.fog.neutral;
    const mode = (params['mode'] as string) ?? 'exp2';
    const near = (params['near'] as number) ?? 100;
    const far = (params['far'] as number) ?? 2000;

    if (!fogState) {
      fogState = { originalFog: scene.fog as THREE.FogExp2 | THREE.Fog | null };
    }

    const fogColor = new THREE.Color(colour);
    if (mode === 'linear') {
      scene.fog = new THREE.Fog(fogColor, near, far);
    } else {
      scene.fog = new THREE.FogExp2(fogColor.getHex(), density);
    }
    notifyChange();
  }

  function disposeFog(): void {
    if (fogState) {
      scene.fog = fogState.originalFog;
      fogState = null;
      notifyChange();
    }
  }

  // ------- CLOUDS -------
  function createClouds(params: Record<string, unknown>): void {
    if (cloudsState) { console.debug('[EnvCtrl] createClouds: already exists, skip'); return; }
    console.info('[EnvCtrl] createClouds: creating sky dome mesh');

    const altitude = (params['altitude'] as number) ?? 2000;
    const opacity = (params['opacity'] as number) ?? 0.8;

    const geometry = new THREE.SphereGeometry(CLOUD_SPHERE_RADIUS, 32, 16);
    const cloudTexture = createCloudTexture();
    const material = new THREE.MeshBasicMaterial({
      ...(cloudTexture ? { map: cloudTexture } : { color: 0xffffff }),
      side: THREE.BackSide,
      transparent: true,
      opacity,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    const camPos = getCameraPosition();
    mesh.position.set(camPos.x, camPos.y, camPos.z + altitude);
    scene.add(mesh);
    cloudsState = { mesh, geometry, material };
    notifyChange();
    if (!reducedMotion) startCloudAnimation();
  }

  function startCloudAnimation(): void {
    let lastTime = performance.now();

    function animateClouds(): void {
      if (!cloudsState || disposed) { cloudAnimFrame = null; return; }
      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      const speed = getLayerParamNum('clouds', 'speed', 2.0);
      const dirDeg = getLayerParamNum('clouds', 'direction', 270);
      const dirRad = dirDeg * Math.PI / 180;
      const camPos = getCameraPosition();
      const altitude = getLayerParamNum('clouds', 'altitude', 2000);
      cloudsState.mesh.position.set(
        camPos.x + Math.sin(dirRad) * speed * dt * 2,
        camPos.y + Math.cos(dirRad) * speed * dt * 2,
        camPos.z + altitude,
      );
      cloudsState.mesh.rotation.z += speed * dt * 0.002;
      notifyChange();
      cloudAnimFrame = requestAnimationFrame(animateClouds);
    }
    cloudAnimFrame = requestAnimationFrame(animateClouds);
  }

  function updateClouds(params: Record<string, unknown>): void {
    if (!cloudsState) return;
    const altitude = (params['altitude'] as number) ?? 2000;
    const opacity = (params['opacity'] as number) ?? 0.8;
    const camPos = getCameraPosition();
    cloudsState.mesh.position.set(camPos.x, camPos.y, camPos.z + altitude);
    cloudsState.material.opacity = opacity;
    notifyChange();
  }

  function disposeClouds(): void {
    if (cloudAnimFrame !== null) { cancelAnimationFrame(cloudAnimFrame); cloudAnimFrame = null; }
    if (!cloudsState) return;
    scene.remove(cloudsState.mesh);
    cloudsState.material.map?.dispose();
    cloudsState.geometry.dispose();
    cloudsState.material.dispose();
    cloudsState = null;
  }

  // ------- WIND -------
  // A horizontal band of flow streaks over the buildings (anchored at the
  // orbit target). Each streak is a thin horizontal quad aligned to the wind
  // direction and drifting along it — reads like a Windfinder flow layer.
  function windBand(): { radius: number; zLo: number; zHi: number; streak: number } {
    const { radius, height, streak } = precipColumn();
    return { radius, zLo: height * 0.12, zHi: height * 0.5, streak: streak * 2 };
  }

  function seedWindParticles(count: number, radius: number, zLo: number, zHi: number, yaw: number): void {
    if (!windState) return;
    const positions = windState.positions;
    const dummy = _sharedDummy;
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * radius;
      positions[i * 3] = Math.cos(a) * r;
      positions[i * 3 + 1] = Math.sin(a) * r;
      positions[i * 3 + 2] = zLo + Math.random() * (zHi - zLo);
      dummy.position.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
      dummy.rotation.set(0, 0, yaw);
      dummy.updateMatrix();
      windState.mesh.setMatrixAt(i, dummy.matrix);
    }
    windState.mesh.instanceMatrix.needsUpdate = true;
  }

  function createWind(params: Record<string, unknown>): void {
    const { radius, zLo, zHi, streak } = windBand();
    const w = getWindParams();
    const yaw = Math.atan2(w.vy, w.vx);
    const viewDist = getViewDistance();

    if (windState) {
      console.warn(`[EnvCtrl] createWind: re-activate (${windState.count} streaks, viewDist=${viewDist.toFixed(0)}m)`);
      windState.mesh.visible = true;
      followAnchorWith(windState.container);
      seedWindParticles(windState.count, radius, zLo, zHi, yaw);
      if (!reducedMotion && windAnimFrame === null) startWindAnimation();
      notifyChange();
      return;
    }

    const densityPreset = (params['particleDensity'] as string) ?? 'medium';
    const counts: Record<string, number> = { low: 400, medium: 1000, high: 2200 };
    const count = Math.min(counts[densityPreset] ?? 1000, WIND_POOL_SIZE);
    const lengthMul = (params['streakLength'] as number) ?? 1;
    const opacity = (params['opacity'] as number) ?? 0.5;
    console.warn(`[EnvCtrl] createWind: creating ${count} flow streaks (target-band, viewDist=${viewDist.toFixed(0)}m, radius=${radius.toFixed(0)}m)`);

    // Horizontal quad lying in XY (normal +Z), elongated along local +X.
    const geometry = new THREE.PlaneGeometry(streak * lengthMul, streak * 0.06);

    const material = new THREE.MeshBasicMaterial({
      color: 0xbcd6f0,
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.InstancedMesh(geometry, material, count);
    mesh.frustumCulled = false;
    mesh.renderOrder = 1000;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    const container = new THREE.Group();
    container.name = 'env-wind';
    container.frustumCulled = false;
    container.add(mesh);
    scene.add(container);

    windState = {
      container,
      mesh,
      geometry,
      material,
      positions: new Float32Array(count * 3),
      count,
    };
    followAnchorWith(container);
    seedWindParticles(count, radius, zLo, zHi, yaw);
    notifyChange();
    if (!reducedMotion) startWindAnimation();
  }

  function startWindAnimation(): void {
    let lastTime = performance.now();
    let frameCount = 0;

    function animateWind(): void {
      if (!windState || disposed) { windAnimFrame = null; return; }
      followAnchorWith(windState.container);

      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      const w = getWindParams();
      const gust = getLayerParam('wind', 'gust') as boolean;
      const gustFactor = gust ? 0.8 + Math.random() * 0.4 : 1.0;
      const speed = w.speed * gustFactor;
      const yaw = Math.atan2(w.vy, w.vx);
      const ux = Math.cos(yaw), uy = Math.sin(yaw);
      const { radius, zLo, zHi } = windBand();
      const dummy = _sharedDummy;

      const pos = windState.positions;
      const n = windState.count;
      for (let i = 0; i < n; i++) {
        const ix = i * 3, iy = ix + 1, iz = ix + 2;
        pos[ix] += ux * speed * dt * 2;
        pos[iy] += uy * speed * dt * 2;
        // Wrap when a streak drifts beyond the band radius.
        if (pos[ix] * pos[ix] + pos[iy] * pos[iy] > radius * radius) {
          const a = Math.random() * Math.PI * 2;
          const r = Math.sqrt(Math.random()) * radius * 0.6;
          // Re-enter from the upwind side.
          pos[ix] = Math.cos(a) * r - ux * radius * 0.4;
          pos[iy] = Math.sin(a) * r - uy * radius * 0.4;
          pos[iz] = zLo + Math.random() * (zHi - zLo);
        }
        dummy.position.set(pos[ix], pos[iy], pos[iz]);
        dummy.rotation.set(0, 0, yaw);
        dummy.updateMatrix();
        windState.mesh.setMatrixAt(i, dummy.matrix);
      }
      windState.mesh.instanceMatrix.needsUpdate = true;
      frameCount++;
      if (frameCount % 2 === 0) notifyChange();
      windAnimFrame = requestAnimationFrame(animateWind);
    }
    windAnimFrame = requestAnimationFrame(animateWind);
  }

  function disposeWind(): void {
    if (windAnimFrame !== null) { cancelAnimationFrame(windAnimFrame); windAnimFrame = null; }
    if (!windState) return;
    windState.container.parent?.remove(windState.container);
    windState.geometry.dispose();
    windState.material.dispose();
    windState.mesh.dispose();
    windState = null;
  }

  // ------- Shared anchoring for precipitation -------
  // Particles live in a Group added to the SCENE (Giro3D does not put its
  // camera in the scene graph, so camera children never render). The group is
  // anchored at the ORBIT TARGET — the ground/buildings the camera looks at —
  // NOT the camera itself, so rain falls over the scene in front of the viewer
  // instead of blocking the lens. Local particle coords stay small (no Web
  // Mercator float jitter at ~15 550 000 m).

  /** World position of the orbit target (falls back to camera). */
  function getAnchorWorld(out: THREE.Vector3): boolean {
    const target = inst.view?.controls?.target as THREE.Vector3 | undefined;
    if (target) { out.copy(target); return true; }
    const cam = getCamera();
    if (cam) { cam.getWorldPosition(out); return true; }
    return false;
  }

  function followAnchorWith(container: THREE.Group): boolean {
    if (!getAnchorWorld(_sharedWorldPos)) return false;
    container.position.copy(_sharedWorldPos);
    container.updateMatrixWorld();
    return true;
  }

  /** Horizontal yaw that turns a +Y-facing quad toward the camera. */
  function billboardYawToCamera(container: THREE.Group): number {
    const cam = getCamera();
    if (!cam) return 0;
    cam.getWorldPosition(_sharedWorldPos);
    const dx = _sharedWorldPos.x - container.position.x;
    const dy = _sharedWorldPos.y - container.position.y;
    return Math.atan2(-dx, dy);
  }

  /** Volume sizing for a precipitation column centred on the orbit target. */
  function precipColumn(): { radius: number; height: number; streak: number } {
    const dist = getViewDistance();
    const radius = Math.max(120, Math.min(2000, dist * 0.9));
    const height = Math.max(120, Math.min(1500, dist * 0.7));
    const streak = Math.max(2, Math.min(14, dist * 0.012));
    return { radius, height, streak };
  }

  // ------- RAIN -------
  function writeRainInstanceMatrices(count: number, yaw: number): void {
    if (!rainState) return;
    const dummy = _sharedDummy;
    const pos = rainState.positions;
    for (let i = 0; i < count; i++) {
      dummy.position.set(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);
      dummy.rotation.set(0, 0, yaw);
      // Per-instance length variance so the streaks don't look uniform.
      dummy.scale.set(1, 1, 0.7 + (i % 7) * 0.1);
      dummy.updateMatrix();
      rainState.mesh.setMatrixAt(i, dummy.matrix);
    }
    dummy.scale.setScalar(1);
    rainState.mesh.instanceMatrix.needsUpdate = true;
  }

  function seedRainParticles(count: number, radius: number, height: number, yaw: number): void {
    if (!rainState) return;
    const positions = rainState.positions;
    const velocities = rainState.velocities;
    for (let i = 0; i < count; i++) {
      // Disc in XY around the target, tall column in +Z above the ground.
      const a = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * radius;
      positions[i * 3] = Math.cos(a) * r;
      positions[i * 3 + 1] = Math.sin(a) * r;
      positions[i * 3 + 2] = Math.random() * height;
      velocities[i] = 60 + Math.random() * 40;
    }
    writeRainInstanceMatrices(count, yaw);
  }

  function createRain(params: Record<string, unknown>): void {
    const intensity = (params['intensity'] as number) ?? 0.5;
    const { radius, height, streak } = precipColumn();
    const viewDist = getViewDistance();

    if (rainState) {
      console.warn(`[EnvCtrl] createRain: re-activate (${rainState.count} particles, viewDist=${viewDist.toFixed(0)}m)`);
      rainState.mesh.visible = true;
      followAnchorWith(rainState.container);
      seedRainParticles(rainState.count, radius, height, billboardYawToCamera(rainState.container));
      if (!reducedMotion && rainAnimFrame === null) startRainAnimation();
      notifyChange();
      return;
    }

    const count = Math.round(1500 + intensity * 10_000);
    const actualCount = Math.min(count, RAIN_POOL_SIZE);
    const sizeMul = (params['dropSize'] as number) ?? 1;
    const opacity = (params['opacity'] as number) ?? 0.5;
    console.warn(`[EnvCtrl] createRain: creating ${actualCount} particles (target-column, viewDist=${viewDist.toFixed(0)}m, radius=${radius.toFixed(0)}m, streak=${(streak * sizeMul).toFixed(1)}m)`);

    // PlaneGeometry lies in XY (normal +Z). Rotate so the quad is vertical:
    // extends along local Z (up/down) and X (width), face along ±Y.
    const geometry = new THREE.PlaneGeometry(streak * 0.05 * sizeMul, streak * sizeMul);
    geometry.rotateX(-Math.PI / 2);

    const material = new THREE.MeshBasicMaterial({
      color: RAIN_COLOR,
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.InstancedMesh(geometry, material, actualCount);
    mesh.frustumCulled = false;
    mesh.renderOrder = 1000;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    const container = new THREE.Group();
    container.name = 'env-rain';
    container.frustumCulled = false;
    container.add(mesh);
    scene.add(container);

    rainState = {
      container,
      mesh,
      geometry,
      material,
      positions: new Float32Array(actualCount * 3),
      velocities: new Float32Array(actualCount),
      count: actualCount,
    };
    followAnchorWith(container);
    seedRainParticles(actualCount, radius, height, billboardYawToCamera(container));
    notifyChange();
    if (!reducedMotion) startRainAnimation();
  }

  function startRainAnimation(): void {
    let lastTime = performance.now();
    let frameCount = 0;

    function animateRain(): void {
      if (!rainState || disposed) { rainAnimFrame = null; return; }
      followAnchorWith(rainState.container);

      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      const pos = rainState.positions;
      const vel = rainState.velocities;
      const n = rainState.count;
      const { radius, height } = precipColumn();
      const yaw = billboardYawToCamera(rainState.container);
      const windCoupled = getLayerParam('rain', 'windCoupled') as boolean;
      const fallSpeed = getLayerParamNum('rain', 'fallSpeed', 1);
      const w = getWindParams();
      const wx = windCoupled ? w.vx : 0;
      const wy = windCoupled ? w.vy : 0;

      for (let i = 0; i < n; i++) {
        const ix = i * 3, iy = ix + 1, iz = ix + 2;
        pos[iz] -= vel[i] * fallSpeed * dt;
        pos[ix] += wx * dt * 0.4;
        pos[iy] += wy * dt * 0.4;
        if (pos[iz] < 0) {
          const a = Math.random() * Math.PI * 2;
          const r = Math.sqrt(Math.random()) * radius;
          pos[ix] = Math.cos(a) * r;
          pos[iy] = Math.sin(a) * r;
          pos[iz] = height + Math.random() * 20;
        }
      }
      writeRainInstanceMatrices(n, yaw);
      frameCount++;
      if (frameCount % 2 === 0) notifyChange();
      rainAnimFrame = requestAnimationFrame(animateRain);
    }
    rainAnimFrame = requestAnimationFrame(animateRain);
  }

  function disposeRain(): void {
    if (rainAnimFrame !== null) { cancelAnimationFrame(rainAnimFrame); rainAnimFrame = null; }
    if (!rainState) return;
    rainState.container.parent?.remove(rainState.container);
    rainState.geometry.dispose();
    rainState.material.dispose();
    rainState.mesh.dispose();
    rainState = null;
  }

  // ------- SNOW -------
  /** Flake geometry for the chosen shape (dot/hex/square). */
  function snowflakeGeometry(shape: string, flake: number): THREE.BufferGeometry {
    switch (shape) {
      case 'square': return new THREE.PlaneGeometry(flake * 1.5, flake * 1.5);
      case 'hex': return new THREE.CircleGeometry(flake, 6);
      case 'dot':
      default: return new THREE.CircleGeometry(flake, 10);
    }
  }

  function createSnow(params: Record<string, unknown>): void {
    const intensity = (params['intensity'] as number) ?? 0.3;
    const { radius, height, streak } = precipColumn();
    const viewDist = getViewDistance();

    if (snowState) {
      console.warn(`[EnvCtrl] createSnow: re-activate (${snowState.count} flakes, viewDist=${viewDist.toFixed(0)}m)`);
      snowState.mesh.visible = true;
      followAnchorWith(snowState.container);
      seedSnowParticles(snowState.count, radius, height);
      if (!reducedMotion && snowAnimFrame === null) startSnowAnimation();
      notifyChange();
      return;
    }

    const count = Math.round(3000 + intensity * 12_000);
    const actualCount = Math.min(count, SNOW_POOL_SIZE);
    const flakeMul = (params['flakeSize'] as number) ?? 1;
    const opacity = (params['opacity'] as number) ?? 0.85;
    const shape = (params['shape'] as string) ?? 'dot';
    const flake = Math.max(0.4, Math.min(8, streak * 0.18 * flakeMul));
    console.warn(`[EnvCtrl] createSnow: creating ${actualCount} flakes (target-column, shape=${shape}, viewDist=${viewDist.toFixed(0)}m, radius=${radius.toFixed(0)}m)`);

    const geometry = snowflakeGeometry(shape, flake);
    const material = new THREE.MeshBasicMaterial({
      color: SNOW_COLOR,
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.InstancedMesh(geometry, material, actualCount);
    mesh.frustumCulled = false;
    mesh.renderOrder = 1000;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    const container = new THREE.Group();
    container.name = 'env-snow';
    container.frustumCulled = false;
    container.add(mesh);
    scene.add(container);
    snowState = {
      container,
      mesh,
      geometry,
      material,
      positions: new Float32Array(actualCount * 3),
      velocities: new Float32Array(actualCount),
      count: actualCount,
    };
    followAnchorWith(container);
    seedSnowParticles(actualCount, radius, height);
    notifyChange();
    if (!reducedMotion) startSnowAnimation();
  }

  function seedSnowParticles(count: number, radius: number, height: number): void {
    if (!snowState) return;
    const positions = snowState.positions;
    const velocities = snowState.velocities;
    const dummy = _sharedDummy;
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * radius;
      positions[i * 3] = Math.cos(a) * r;
      positions[i * 3 + 1] = Math.sin(a) * r;
      positions[i * 3 + 2] = Math.random() * height;
      velocities[i] = 6 + Math.random() * 8;
      dummy.position.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.setScalar(0.6 + (i % 5) * 0.16);
      dummy.updateMatrix();
      snowState.mesh.setMatrixAt(i, dummy.matrix);
    }
    dummy.scale.setScalar(1);
    snowState.mesh.instanceMatrix.needsUpdate = true;
  }

  function startSnowAnimation(): void {
    let lastTime = performance.now();
    let frameCount = 0;

    function animateSnow(): void {
      if (!snowState || disposed) { snowAnimFrame = null; return; }
      followAnchorWith(snowState.container);

      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      const pos = snowState.positions;
      const vel = snowState.velocities;
      const n = snowState.count;
      const dummy = _sharedDummy;
      const { radius, height } = precipColumn();
      const windCoupled = getLayerParam('snow', 'windCoupled') as boolean;
      const fallSpeed = getLayerParamNum('snow', 'fallSpeed', 1);
      const sway = getLayerParamNum('snow', 'sway', 1);
      const w = getWindParams();
      const wx = windCoupled ? w.vx * 0.6 : 0;
      const wy = windCoupled ? w.vy * 0.6 : 0;

      for (let i = 0; i < n; i++) {
        const ix = i * 3, iy = ix + 1, iz = ix + 2;
        pos[iz] -= vel[i] * fallSpeed * dt;
        pos[ix] += (wx + (Math.random() - 0.5) * 2 * sway) * dt;
        pos[iy] += (wy + (Math.random() - 0.5) * 2 * sway) * dt;
        if (pos[iz] < 0) {
          const a = Math.random() * Math.PI * 2;
          const r = Math.sqrt(Math.random()) * radius;
          pos[ix] = Math.cos(a) * r;
          pos[iy] = Math.sin(a) * r;
          pos[iz] = height + Math.random() * 20;
        }
        dummy.position.set(pos[ix], pos[iy], pos[iz]);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.setScalar(0.6 + (i % 5) * 0.16);
        dummy.updateMatrix();
        snowState.mesh.setMatrixAt(i, dummy.matrix);
      }
      dummy.scale.setScalar(1);
      snowState.mesh.instanceMatrix.needsUpdate = true;
      frameCount++;
      if (frameCount % 2 === 0) notifyChange();
      snowAnimFrame = requestAnimationFrame(animateSnow);
    }
    snowAnimFrame = requestAnimationFrame(animateSnow);
  }

  function disposeSnow(): void {
    if (snowAnimFrame !== null) { cancelAnimationFrame(snowAnimFrame); snowAnimFrame = null; }
    if (!snowState) return;
    snowState.container.parent?.remove(snowState.container);
    snowState.geometry.dispose();
    snowState.material.dispose();
    snowState.mesh.dispose();
    snowState = null;
  }

  // ------- LIGHTNING -------
  function createLightning(params: Record<string, unknown>): void {
    if (lightningState) { console.debug('[EnvCtrl] createLightning: already exists, skip'); return; }
    console.info('[EnvCtrl] createLightning: creating AmbientLight flash');

    const frequency = (params['frequency'] as number) ?? 2.0;
    const ambient = new THREE.AmbientLight(0xffffff, 0);
    scene.add(ambient);

    lightningState = { ambient, schedulerHandle: null, flashHandle: null };
    scheduleLightningFlash(frequency, (params['intensity'] as number) ?? 0.8);
    notifyChange();
  }

  function scheduleLightningFlash(frequencyPerMinute: number, intensity: number): void {
    if (!lightningState || disposed) return;
    const meanInterval = 60_000 / frequencyPerMinute;
    const next = -Math.log(Math.random()) * meanInterval;

    lightningState.schedulerHandle = setTimeout(() => {
      triggerFlash(intensity);
      scheduleLightningFlash(frequencyPerMinute, intensity);
    }, next);
  }

  function triggerFlash(peakIntensity: number): void {
    if (!lightningState || disposed) return;
    const t0 = performance.now();
    const ambient = lightningState.ambient;

    function pulse(): void {
      if (!lightningState || disposed) return;
      const elapsed = performance.now() - t0;
      if (elapsed < LIGHTNING_RAMP_MS) {
        ambient.intensity = peakIntensity * (elapsed / LIGHTNING_RAMP_MS);
      } else if (elapsed < LIGHTNING_RAMP_MS + LIGHTNING_DECAY_MS) {
        const decayProgress = (elapsed - LIGHTNING_RAMP_MS) / LIGHTNING_DECAY_MS;
        ambient.intensity = peakIntensity * (1 - decayProgress);
      } else {
        ambient.intensity = 0;
        lightningState.flashHandle = null;
        notifyChange();
        return;
      }
      notifyChange();
      lightningState.flashHandle = requestAnimationFrame(pulse);
    }
    lightningState.flashHandle = requestAnimationFrame(pulse);
  }

  function disposeLightning(): void {
    if (!lightningState) return;
    if (lightningState.schedulerHandle !== null) clearTimeout(lightningState.schedulerHandle);
    if (lightningState.flashHandle !== null) cancelAnimationFrame(lightningState.flashHandle);
    scene.remove(lightningState.ambient);
    lightningState = null;
  }

  // ------- DARKMODE -------
  function applyDarkmode(params: Record<string, unknown>): void {
    const enabled = (params['enabled'] as boolean) ?? false;
    if (enabled) {
      darkmodeSnapshot = new Map();
      for (const [id, state] of layers) {
        if (state.kind === 'sun' || state.kind === 'moon' || state.kind === 'stars') {
          darkmodeSnapshot.set(id, { visible: state.visible, params: { ...state.params } });
        }
      }
      for (const [id, state] of layers) {
        if (state.kind === 'sun') setEnvironmentLayerVisible(id, false);
        if (state.kind === 'moon') setEnvironmentLayerVisible(id, true);
        if (state.kind === 'stars') setEnvironmentLayerVisible(id, true);
      }
    } else if (darkmodeSnapshot) {
      for (const [id, snap] of darkmodeSnapshot) {
        setEnvironmentLayerVisible(id, snap.visible);
      }
      darkmodeSnapshot = null;
    }
  }

  // ------- TIME-OF-DAY -------
  function applyTimeOfDay(params: Record<string, unknown>): void {
    const mode = (params['mode'] as string) ?? 'manual';
    const currentHour = (params['currentHour'] as number) ?? 13.0;

    if (todAnimationHandle !== null) {
      cancelAnimationFrame(todAnimationHandle);
      todAnimationHandle = null;
    }

    if (mode === 'system-clock') {
      startTimeOfDayLoop();
    } else if (mode === 'manual') {
      updateTimeForHour(currentHour);
    }
  }

  function startTimeOfDayLoop(): void {
    if (disposed) return;
    const now = new Date();
    const hour = now.getHours() + now.getMinutes() / 60;
    updateTimeForHour(hour);
    todAnimationHandle = requestAnimationFrame(() => startTimeOfDayLoop());
  }

  /** Daylight strength 0..1 across the day (0 at night, 1 midday). */
  function daylightFactor(hour: number): number {
    if (hour <= 5 || hour >= 21) return 0;
    if (hour < 8) return (hour - 5) / 3;     // dawn ramp 5→8
    if (hour > 18) return (21 - hour) / 3;   // dusk ramp 18→21
    return 1;
  }

  /** Interpolated sky colour for the time of day. */
  function skyColorForHour(hour: number): THREE.Color {
    const night = new THREE.Color(SKY_NIGHT);
    const day = new THREE.Color(SKY_CLEAR);
    const dusk = new THREE.Color(SKY_DUSK);
    if (hour <= 5 || hour >= 21) return night;
    if (hour < 7) return night.lerp(dusk, (hour - 5) / 2);
    if (hour < 9) return dusk.lerp(day, (hour - 7) / 2);
    if (hour <= 17) return day;
    if (hour < 19) return day.lerp(dusk, (hour - 17) / 2);
    return dusk.lerp(night, (hour - 19) / 2);
  }

  function updateTimeForHour(hour: number): void {
    for (const state of layers.values()) {
      if (state.kind === 'sun' && state.visible) {
        state.params['hour'] = hour;
        updateSunPosition(state.params);
        if (sunState) {
          const base = (state.params['intensity'] as number) ?? 2.0;
          sunState.light.intensity = base * daylightFactor(hour);
        }
      }
    }
    const skyVisible = [...layers.values()].some((s) => s.kind === 'sky' && s.visible);
    if (skyVisible) {
      scene.background = skyColorForHour(hour);
    }
    notifyChange();
  }

  // ------- APPLY LAYER (main dispatch) -------
  function applyEnvironmentLayer(
    layerId: string,
    kind: EnvironmentKind,
    params: Record<string, unknown>,
    options?: { visible?: boolean },
  ): void {
    const existing = layers.get(layerId);
    const visible = options?.visible ?? existing?.visible ?? false;
    console.info(`[EnvCtrl] applyEnvironmentLayer: ${layerId} kind=${kind} visible=${visible}`, params);
    layers.set(layerId, {
      kind,
      params: { ...params },
      visible,
      opacity: existing?.opacity ?? 1,
    });

    const state = layers.get(layerId)!;
    if (!state.visible) return;

    switch (kind) {
      case 'sky': applySky(params); break;
      case 'sun': createSun(params); break;
      case 'moon': createMoon(params); break;
      case 'stars': createStars(params); break;
      case 'fog': applyFog(params); break;
      case 'clouds': createClouds(params); break;
      case 'wind': createWind(params); break;
      case 'rain': createRain(params); break;
      case 'snow': createSnow(params); break;
      case 'lightning': createLightning(params); break;
      case 'darkmode': applyDarkmode(params); break;
      case 'time-of-day': applyTimeOfDay(params); break;
    }
  }

  // ------- SET PARAMETER -------
  function setEnvironmentParameter(layerId: string, key: string, value: unknown): void {
    const state = layers.get(layerId);
    if (!state) return;
    state.params[key] = value;

    if (!state.visible) return;

    switch (state.kind) {
      case 'sky': applySky(state.params); break;
      case 'sun': {
        if (key === 'hour') updateSunPosition(state.params);
        else if (key === 'intensity' && sunState) sunState.light.intensity = (value as number);
        else if (key === 'color' && sunState) {
          const colors: Record<string, number> = { warm: SUN_WARM, neutral: SUN_NEUTRAL, cool: SUN_COOL };
          sunState.light.color.setHex(colors[value as string] ?? SUN_NEUTRAL);
        }
        notifyChange();
        break;
      }
      case 'moon': {
        if (moonState) {
          if (key === 'intensity' || key === 'phase') {
            const intensityMul = (state.params['intensity'] as number) ?? 0.5;
            const phase = (state.params['phase'] as number) ?? 0.5;
            moonState.light.intensity = 0.003 * intensityMul * Math.sin(phase * Math.PI);
          }
          if (key === 'color') {
            const colors: Record<string, number> = { coldBlue: MOON_COOL, neutral: MOON_NEUTRAL };
            moonState.light.color.setHex(colors[value as string] ?? MOON_COOL);
          }
          notifyChange();
        }
        break;
      }
      case 'fog': applyFog(state.params); break;
      case 'clouds': updateClouds(state.params); break;
      case 'rain': {
        if (!rainState) break;
        if (key === 'opacity') { rainState.material.opacity = (value as number); notifyChange(); }
        else if (key === 'intensity' || key === 'dropSize') { disposeRain(); createRain(state.params); }
        // fallSpeed + windCoupled are read live each frame — no rebuild needed.
        break;
      }
      case 'snow': {
        if (!snowState) break;
        if (key === 'opacity') { snowState.material.opacity = (value as number); notifyChange(); }
        else if (key === 'intensity' || key === 'flakeSize' || key === 'shape') { disposeSnow(); createSnow(state.params); }
        // fallSpeed + sway + windCoupled are read live each frame.
        break;
      }
      case 'wind': {
        if (!windState) break;
        if (key === 'opacity') { windState.material.opacity = (value as number); notifyChange(); }
        else if (key === 'particleDensity' || key === 'streakLength') { disposeWind(); createWind(state.params); }
        // speed + direction + gust are read live each frame.
        break;
      }
      case 'lightning': {
        disposeLightning();
        createLightning(state.params);
        break;
      }
      case 'time-of-day': applyTimeOfDay(state.params); break;
      default: break;
    }
  }

  // ------- SET VISIBILITY -------
  function setEnvironmentLayerVisible(layerId: string, visible: boolean): void {
    const state = layers.get(layerId);
    if (!state) {
      console.warn(`[EnvCtrl] setVisible: layer "${layerId}" NOT FOUND in registry. Registered:`, [...layers.keys()]);
      return;
    }
    console.warn(`[EnvCtrl] setVisible: ${layerId} (${state.kind}) → ${visible}`);
    state.visible = visible;

    if (visible) {
      switch (state.kind) {
        case 'sky': applySky(state.params); break;
        case 'sun': createSun(state.params); break;
        case 'moon': createMoon(state.params); break;
        case 'stars': createStars(state.params); break;
        case 'fog': applyFog(state.params); break;
        case 'clouds': createClouds(state.params); break;
        case 'wind': createWind(state.params); break;
        case 'rain': createRain(state.params); break;
        case 'snow': createSnow(state.params); break;
        case 'lightning': createLightning(state.params); break;
        case 'darkmode': applyDarkmode(state.params); break;
        case 'time-of-day': applyTimeOfDay(state.params); break;
      }
    } else {
      switch (state.kind) {
        case 'sky': disposeSky(); break;
        case 'sun': disposeSun(); break;
        case 'moon': disposeMoon(); break;
        case 'stars': disposeStars(); break;
        case 'fog': disposeFog(); break;
        case 'clouds': disposeClouds(); break;
        case 'wind': disposeWind(); break;
        case 'rain': disposeRain(); break;
        case 'snow': disposeSnow(); break;
        case 'lightning': disposeLightning(); break;
        case 'darkmode': applyDarkmode({ enabled: false }); break;
        case 'time-of-day': {
          if (todAnimationHandle !== null) {
            cancelAnimationFrame(todAnimationHandle);
            todAnimationHandle = null;
          }
          break;
        }
        default: break;
      }
    }
    notifyChange();
  }

  // ------- SET OPACITY -------
  function setEnvironmentLayerOpacity(layerId: string, opacity: number): void {
    const state = layers.get(layerId);
    if (!state) return;
    state.opacity = opacity;

    if (!state.visible) return;

    switch (state.kind) {
      case 'sun': {
        if (sunState) {
          const baseIntensity = (state.params['intensity'] as number) ?? 2.0;
          sunState.light.intensity = baseIntensity * opacity;
        }
        break;
      }
      case 'moon': {
        if (moonState) {
          const intensityMul = (state.params['intensity'] as number) ?? 0.5;
          const phase = (state.params['phase'] as number) ?? 0.5;
          moonState.light.intensity = 0.003 * intensityMul * Math.sin(phase * Math.PI) * opacity;
        }
        break;
      }
      case 'fog': {
        const baseDensity = (state.params['density'] as number) ?? 0.0002;
        if (scene.fog && 'density' in scene.fog) {
          (scene.fog as THREE.FogExp2).density = baseDensity * opacity;
        }
        break;
      }
      case 'rain': {
        if (rainState) rainState.material.opacity = 0.5 * opacity;
        break;
      }
      case 'snow': {
        if (snowState) snowState.material.opacity = 0.8 * opacity;
        break;
      }
      case 'wind': {
        if (windState) windState.material.opacity = 0.3 * opacity;
        break;
      }
      case 'clouds': {
        if (cloudsState) cloudsState.material.opacity = ((state.params['opacity'] as number) ?? 0.8) * opacity;
        break;
      }
      case 'stars': {
        if (starsState) starsState.material.opacity = opacity;
        break;
      }
      case 'lightning': {
        break;
      }
      default: break;
    }
    notifyChange();
  }

  // ------- DISPOSE ALL -------
  function dispose(): void {
    disposed = true;
    if (todAnimationHandle !== null) cancelAnimationFrame(todAnimationHandle);
    if (rainAnimFrame !== null) cancelAnimationFrame(rainAnimFrame);
    if (snowAnimFrame !== null) cancelAnimationFrame(snowAnimFrame);
    if (windAnimFrame !== null) cancelAnimationFrame(windAnimFrame);
    if (cloudAnimFrame !== null) cancelAnimationFrame(cloudAnimFrame);
    if (starTwinkleFrame !== null) cancelAnimationFrame(starTwinkleFrame);

    disposeSky();
    disposeSun();
    disposeMoon();
    disposeStars();
    disposeFog();
    disposeClouds();
    disposeWind();
    disposeRain();
    disposeSnow();
    disposeLightning();
    layers.clear();
  }

  return {
    applyEnvironmentLayer,
    setEnvironmentParameter,
    setEnvironmentLayerVisible,
    setEnvironmentLayerOpacity,
    dispose,
  };
}
